/**
 * Sweep-group detection and smart naming for DAG run nodes.
 *
 * Groups sibling runs that belong to the same parameter sweep, computes
 * short display names that highlight only the varying parameter(s), and
 * produces collapsed "group" nodes that can replace individual runs in
 * the React Flow graph.
 */

import type { DAGNode, DAGEdge } from "../../api/artifacts";
import type { Node, Edge } from "@xyflow/react";
import {
  ALL_DATA_NODE_WIDTH,
  REF_QTY_GROUP_WIDTH,
  getRefQuantityGroupSize,
  getSweepGroupSize,
  getSourceGroupSize,
} from "./dagNodeSizing";

// ── public types ────────────────────────────────────────────────────

export interface SweepGroup {
  /** Unique identifier – sweep_id from metadata or a synthetic id. */
  groupId: string;
  /** Human-readable title, e.g. "Alpha Sweep (M=0.3, β=0)". */
  title: string;
  /** The parameter keys that vary across the group. */
  varyingKeys: string[];
  /** Shared (constant) parameter values across the group. */
  sharedParams: Record<string, unknown>;
  /** Run nodes in this group, sorted by the primary varying param. */
  runs: DAGNode[];
  /** Short display labels for each run, same order as `runs`. */
  smartNames: string[];
  /** IDs of parent nodes in the DAG (edges pointing into this group). */
  parentIds: string[];
}

// ── parameter display helpers ───────────────────────────────────────

const PARAM_SYMBOLS: Record<string, string> = {
  alpha_deg: "α",
  alpha: "α",
  beta_deg: "β",
  beta: "β",
  mach: "M",
  velocity_magnitude: "V",
  reynolds: "Re",
  RPM: "RPM",
  rpm: "RPM",
  propeller_rpm: "RPM",
  n_actuator_disks: "disks",
};

/**
 * Known input operating-condition keys. Used by the heuristic grouping
 * to ignore output fields (CL, CD, etc.) when deciding whether runs
 * form a sweep. Only these keys are diffed in the fallback path.
 */
const INPUT_PARAM_KEYS = new Set([
  "alpha_deg",
  "alpha",
  "beta_deg",
  "beta",
  "mach",
  "velocity_magnitude",
  "reynolds",
  "RPM",
  "rpm",
  "propeller_rpm",
  "n_actuator_disks",
]);

function paramSymbol(key: string): string {
  return PARAM_SYMBOLS[key] ?? key;
}

function paramLabel(key: string): string {
  const labels: Record<string, string> = {
    alpha_deg: "Alpha",
    beta_deg: "Beta",
    mach: "Mach",
    velocity_magnitude: "Velocity",
    reynolds: "Reynolds",
    RPM: "RPM",
    rpm: "RPM",
    propeller_rpm: "Propeller RPM",
    n_actuator_disks: "Actuator Disks",
  };
  return labels[key] ?? key;
}

function formatValue(v: unknown): string {
  if (typeof v === "number") {
    return Number.isInteger(v) ? String(v) : v.toPrecision(4).replace(/\.?0+$/, "");
  }
  return String(v ?? "");
}

// ── core detection ──────────────────────────────────────────────────

/**
 * Detect groups of run nodes that form a parameter sweep.
 *
 * Primary signal: nodes sharing `metadata.sweep_id`.
 * Fallback heuristic: runs whose input flow parameters differ in at
 * most 2 values (grouped by shared-parameter fingerprint, regardless
 * of DAG parentage).
 */
export function detectSweepGroups(
  nodes: DAGNode[],
  edges: DAGEdge[],
): SweepGroup[] {
  const runs = nodes.filter((n) => n.kind === "run");
  if (runs.length < 2) return [];

  const childToParents = buildChildToParents(edges);
  const grouped = new Set<string>();
  const groups: SweepGroup[] = [];

  // ── pass 1: group by sweep_id ──
  const bySweepId = new Map<string, DAGNode[]>();
  for (const run of runs) {
    const sid = (run.metadata as Record<string, unknown> | undefined)?.sweep_id;
    if (typeof sid === "string") {
      let arr = bySweepId.get(sid);
      if (!arr) {
        arr = [];
        bySweepId.set(sid, arr);
      }
      arr.push(run);
    }
  }

  for (const [sid, members] of bySweepId) {
    if (members.length < 2) continue;
    const group = buildGroup(sid, members, childToParents, INPUT_PARAM_KEYS);
    if (group) {
      groups.push(group);
      for (const r of members) grouped.add(r.id);
    }
  }

  // ── pass 2: heuristic grouping by shared flow parameters ──
  // Group runs whose input params match except for 1–2 swept keys,
  // regardless of which mesh/parent they depend on.
  const ungrouped = runs.filter((r) => !grouped.has(r.id));
  const claimed = new Set<string>();
  const inputKeys = [...INPUT_PARAM_KEYS].sort();

  // Pre-compute params once to avoid re-parsing 28× per run
  const runParamsCache = new Map<string, Record<string, unknown>>();
  for (const run of ungrouped) runParamsCache.set(run.id, getRunParams(run));

  for (const excludeCombo of paramCombinations(inputKeys, 1, 2)) {
    const excludeSet = new Set(excludeCombo);
    const byFp = new Map<string, DAGNode[]>();

    for (const run of ungrouped) {
      if (claimed.has(run.id)) continue;
      const params = runParamsCache.get(run.id)!;
      if (excludeCombo.some((k) => !(k in params))) continue;

      const fp = inputKeys
        .filter((k) => !excludeSet.has(k))
        .map((k) => `${k}=${params[k]}`)
        .join("|");

      let arr = byFp.get(fp);
      if (!arr) {
        arr = [];
        byFp.set(fp, arr);
      }
      arr.push(run);
    }

    for (const [, members] of byFp) {
      if (members.length < 2) continue;
      const inputVarying = findVaryingKeys(members, INPUT_PARAM_KEYS);
      if (inputVarying.length < 1 || inputVarying.length > 2) continue;

      const syntheticId = `heuristic-${members.map((s) => s.id).sort().join("-").slice(0, 36)}`;
      const group = buildGroup(syntheticId, members, childToParents, INPUT_PARAM_KEYS);
      if (group) {
        groups.push(group);
        for (const r of members) claimed.add(r.id);
      }
    }
  }

  // ── pass 3: merge groups with same varying keys and compatible shared params ──
  const merged = mergeCompatibleGroups(groups);

  // ── pass 4: absorb remaining ungrouped runs into compatible groups ──
  const allGroupedIds = new Set<string>();
  for (const g of merged) for (const r of g.runs) allGroupedIds.add(r.id);
  const remaining = runs.filter((r) => !allGroupedIds.has(r.id) && !claimed.has(r.id));
  const absorbed = absorbSingles(merged, remaining, childToParents);

  // ── pass 5: overflow fallback ─────────────────────────────────────
  // Rendering every individual run as its own React Flow node breaks ELK
  // layout and the browser at ~2000+ nodes. If too many runs stayed
  // ungrouped after pass 4, bucket them into a synthetic group by their
  // nearest mesh/geometry ancestor so the DAG stays legible. The sweep
  // node is rendered non-expandable (see `applySweepGroups`
  // `maxExpandableSize`).
  const claimedAfter = new Set<string>();
  for (const g of absorbed) for (const r of g.runs) claimedAfter.add(r.id);
  const stillUngrouped = runs.filter((r) => !claimedAfter.has(r.id));
  if (stillUngrouped.length <= OVERFLOW_UNGROUPED_THRESHOLD) return absorbed;

  // Bucket by the first ancestor id we find (geometry/mesh). Falls back to
  // a single "ungrouped" bucket when a run has no parents.
  const byAncestor = new Map<string, DAGNode[]>();
  for (const r of stillUngrouped) {
    const parents = childToParents.get(r.id) ?? [];
    const key = parents[0] ?? "__orphan__";
    const list = byAncestor.get(key);
    if (list) list.push(r); else byAncestor.set(key, [r]);
  }
  for (const [key, members] of byAncestor) {
    if (members.length < 2) continue;
    const groupId = `overflow-${key.slice(0, 36)}`;
    absorbed.push({
      groupId,
      title: `${members.length} runs`,
      runs: members,
      smartNames: members.map((m) => m.name ?? m.id.slice(0, 8)),
      sharedParams: {},
      varyingKeys: [],
      parentIds: key === "__orphan__" ? [] : [key],
    });
  }
  return absorbed;
}

/** When more than this many runs remain ungrouped after normal sweep
 *  detection, fall back to a coarse overflow grouping by parent ancestor
 *  to keep the DAG render under control. */
const OVERFLOW_UNGROUPED_THRESHOLD = 300;

// ── pass 3: merge compatible groups ─────────────────────────────────

/**
 * Merge groups that sweep the same parameter(s) and have compatible
 * shared parameters — i.e. their constant flow conditions match even
 * if they come from different meshes or batches.
 */
function mergeCompatibleGroups(groups: SweepGroup[]): SweepGroup[] {
  if (groups.length < 2) return groups;

  const consumed = new Set<number>();
  const result: SweepGroup[] = [];

  for (let i = 0; i < groups.length; i++) {
    if (consumed.has(i)) continue;

    const gi = groups[i]!;
    const varyKey = gi.varyingKeys.join(",");
    const parentSet = new Set(gi.parentIds);
    let allRuns = [...gi.runs];
    let needsMerge = false;

    const seenVaryingValues = new Set(
      gi.runs.map((r) => varyingFingerprint(r, gi.varyingKeys)),
    );

    for (let j = i + 1; j < groups.length; j++) {
      if (consumed.has(j)) continue;
      const gj = groups[j]!;
      if (gj.varyingKeys.join(",") !== varyKey) continue;
      if (!sharedParamsCompatible(gi.sharedParams, gj.sharedParams)) continue;
      if (gj.runs.some((r) => seenVaryingValues.has(varyingFingerprint(r, gj.varyingKeys)))) continue;

      consumed.add(j);
      allRuns.push(...gj.runs);
      for (const r of gj.runs) seenVaryingValues.add(varyingFingerprint(r, gj.varyingKeys));
      for (const p of gj.parentIds) parentSet.add(p);
      needsMerge = true;
    }

    if (!needsMerge) {
      result.push(gi);
      continue;
    }

    const varying = gi.varyingKeys;
    const shared = computeSharedParams(allRuns, varying);
    const sorted = sortByVarying(allRuns, varying);
    const smartNames = sorted.map((r) => smartNameFor(r, varying));
    const title = buildGroupTitle(varying, shared, allRuns.length);

    result.push({
      groupId: `merged-${gi.groupId}`,
      title,
      varyingKeys: varying,
      sharedParams: shared,
      runs: sorted,
      smartNames,
      parentIds: [...parentSet],
    });
  }

  return result;
}

// ── pass 4: absorb singles ──────────────────────────────────────────

/**
 * Fold remaining ungrouped runs into an existing group when the run's
 * input params are compatible — i.e. it matches the group's shared
 * (constant) flow conditions and has a value for every varying key.
 */
function absorbSingles(
  groups: SweepGroup[],
  remaining: DAGNode[],
  childToParents: Map<string, string[]>,
): SweepGroup[] {
  if (remaining.length === 0) return groups;

  const absorbed = new Set<string>();
  const dirty = new Set<number>();

  for (const run of remaining) {
    const params = getRunParams(run);
    for (let gi = 0; gi < groups.length; gi++) {
      const g = groups[gi]!;
      if (!g.varyingKeys.every((k) => k in params)) continue;
      const compatible = Object.entries(g.sharedParams)
        .filter(([k]) => INPUT_PARAM_KEYS.has(k))
        .every(([k, v]) => params[k] === v);
      if (!compatible) continue;
      const fp = varyingFingerprint(run, g.varyingKeys);
      if (g.runs.some((r) => varyingFingerprint(r, g.varyingKeys) === fp)) continue;

      g.runs.push(run);
      absorbed.add(run.id);
      dirty.add(gi);
      break;
    }
  }

  if (dirty.size === 0) return groups;

  return groups.map((g, i) => {
    if (!dirty.has(i)) return g;
    const varying = g.varyingKeys;
    const sorted = sortByVarying(g.runs, varying);
    const shared = computeSharedParams(sorted, varying);
    const smartNames = sorted.map((r) => smartNameFor(r, varying));
    const parentIdSet = new Set(g.parentIds);
    for (const r of sorted) {
      for (const pid of childToParents.get(r.id) ?? []) parentIdSet.add(pid);
    }
    return {
      ...g,
      runs: sorted,
      smartNames,
      sharedParams: shared,
      parentIds: [...parentIdSet],
      title: buildGroupTitle(varying, shared, sorted.length),
    };
  });
}

// ── group construction ──────────────────────────────────────────────

function buildGroup(
  groupId: string,
  members: DAGNode[],
  childToParents: Map<string, string[]>,
  inputOnlyKeys?: Set<string>,
): SweepGroup | null {
  const varying = findVaryingKeys(members, inputOnlyKeys);
  if (varying.length === 0) return null;

  const shared = computeSharedParams(members, varying);
  const sorted = sortByVarying(members, varying);
  const smartNames = sorted.map((r) => smartNameFor(r, varying));

  const parentIdSet = new Set<string>();
  for (const r of members) {
    for (const pid of childToParents.get(r.id) ?? []) {
      parentIdSet.add(pid);
    }
  }

  const title = buildGroupTitle(varying, shared, members.length);

  return {
    groupId,
    title,
    varyingKeys: varying,
    sharedParams: shared,
    runs: sorted,
    smartNames,
    parentIds: [...parentIdSet],
  };
}

function buildGroupTitle(
  varying: string[],
  shared: Record<string, unknown>,
  count: number,
): string {
  const sweepPart = varying.map((k) => paramLabel(k)).join(" × ");
  const sharedParts = Object.entries(shared)
    .filter(([k]) => k in PARAM_SYMBOLS)
    .map(([k, v]) => `${paramSymbol(k)}=${formatValue(v)}`);
  const sharedStr = sharedParts.length ? ` (${sharedParts.join(", ")})` : "";
  return `${sweepPart} Sweep${sharedStr} · ${count} runs`;
}

// ── parameter diffing ───────────────────────────────────────────────

function getRunParams(node: DAGNode): Record<string, unknown> {
  const vj = node.value_json;
  if (vj && typeof vj === "object") return vj as Record<string, unknown>;
  return {};
}

/**
 * Return param keys whose values differ across the given run nodes.
 *
 * When `onlyKeys` is provided, only those keys are considered. This is
 * used by the heuristic grouping path to ignore output fields (CL, CD, ...)
 * that always differ between runs.
 */
function findVaryingKeys(runs: DAGNode[], onlyKeys?: Set<string>): string[] {
  if (runs.length < 2) return [];

  const allKeys = new Set<string>();
  const paramSets = runs.map((r) => {
    const p = getRunParams(r);
    for (const k of Object.keys(p)) {
      if (!onlyKeys || onlyKeys.has(k)) allKeys.add(k);
    }
    return p;
  });

  const varying: string[] = [];
  for (const key of allKeys) {
    const values = paramSets.map((p) => p[key]);
    if (values.some((v) => v === undefined)) continue;
    const first = values[0];
    if (values.some((v) => v !== first)) {
      varying.push(key);
    }
  }

  return varying.sort();
}

function computeSharedParams(
  runs: DAGNode[],
  varying: string[],
): Record<string, unknown> {
  if (runs.length === 0) return {};
  const varySet = new Set(varying);
  const paramSets = runs.map(getRunParams);
  const shared: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(paramSets[0]!)) {
    if (varySet.has(k) || typeof v === "object") continue;
    if (paramSets.every((p) => p[k] === v)) shared[k] = v;
  }
  return shared;
}

function sortByVarying(runs: DAGNode[], varying: string[]): DAGNode[] {
  const primaryKey = varying[0] ?? "";
  return [...runs].sort((a, b) => {
    const pa = getRunParams(a);
    const pb = getRunParams(b);
    for (const key of [primaryKey, ...varying.slice(1)]) {
      const va = Number(pa[key] ?? 0);
      const vb = Number(pb[key] ?? 0);
      if (va !== vb) return va - vb;
    }
    return 0;
  });
}

function smartNameFor(run: DAGNode, varying: string[]): string {
  const params = getRunParams(run);
  return varying
    .map((k) => `${paramSymbol(k)}=${formatValue(params[k])}`)
    .join(", ");
}

function varyingFingerprint(run: DAGNode, varyingKeys: string[]): string {
  const p = getRunParams(run);
  return varyingKeys.map((k) => `${k}=${p[k]}`).join("|");
}

// ── combinatorial helpers ───────────────────────────────────────────

/** Yield all k-element subsets of `arr` for each k in [minK, maxK]. */
function paramCombinations(arr: string[], minK: number, maxK: number): string[][] {
  const result: string[][] = [];
  function pick(start: number, combo: string[]) {
    if (combo.length >= minK) result.push([...combo]);
    if (combo.length === maxK) return;
    for (let i = start; i < arr.length; i++) {
      combo.push(arr[i]!);
      pick(i + 1, combo);
      combo.pop();
    }
  }
  pick(0, []);
  return result;
}

/** True when all keys present in both maps have equal values. */
function sharedParamsCompatible(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): boolean {
  for (const [k, v] of Object.entries(a)) {
    if (k in b && b[k] !== v) return false;
  }
  return true;
}

// ── edge helpers ────────────────────────────────────────────────────

function buildChildToParents(edges: DAGEdge[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const e of edges) {
    let arr = map.get(e.child_id);
    if (!arr) {
      arr = [];
      map.set(e.child_id, arr);
    }
    arr.push(e.parent_id);
  }
  return map;
}

// ── React Flow node/edge transformation ─────────────────────────────

export interface CollapseResult {
  nodes: Node[];
  edges: Edge[];
}

/**
 * Transform React Flow nodes/edges by collapsing sweep groups into
 * single group nodes, or applying smart names to expanded groups.
 */
export function applySweepGroups(
  flowNodes: Node[],
  flowEdges: Edge[],
  groups: SweepGroup[],
  expandedGroups: Set<string>,
  onToggleGroup: (groupId: string) => void,
  onSelectRun?: (nodeId: string) => void,
  /** When a group's run count exceeds this, its React Flow node is rendered
   *  un-expandable (visual hint + ignored onClick). Collapsing always works. */
  maxExpandableSize?: number,
): CollapseResult {
  if (groups.length === 0) return { nodes: flowNodes, edges: flowEdges };

  const runToGroup = new Map<string, SweepGroup>();
  for (const g of groups) {
    for (const run of g.runs) runToGroup.set(run.id, g);
  }

  const newNodes: Node[] = [];
  const addedGroups = new Set<string>();

  for (const node of flowNodes) {
    const group = runToGroup.get(node.id);
    if (!group) {
      newNodes.push(node);
      continue;
    }

    if (addedGroups.has(group.groupId)) continue;
    addedGroups.add(group.groupId);

    const expanded = expandedGroups.has(group.groupId);
    const expandable = maxExpandableSize === undefined || group.runs.length <= maxExpandableSize;
    const sweepSize = getSweepGroupSize({
      expanded,
      runCount: group.runs.length,
    });
    newNodes.push({
      id: `sweep-${group.groupId}`,
      type: "sweepGroup",
      data: {
        label: group.title,
        group,
        expanded,
        expandable,
        onToggle: () => onToggleGroup(group.groupId),
        onSelectRun,
      },
      position: { x: 0, y: 0 },
      style: expanded
        ? {
            borderRadius: 12,
            fontSize: 12,
            width: sweepSize.width,
            height: sweepSize.height,
            boxSizing: "border-box" as const,
          }
        : {
            background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
            color: "#fff",
            borderRadius: 8,
            padding: "8px 14px",
            fontSize: 12,
            border: "2px solid rgba(255,255,255,0.3)",
            boxShadow: "0 2px 8px rgba(34,197,94,0.3)",
            width: sweepSize.width,
            height: sweepSize.height,
            boxSizing: "border-box" as const,
          },
    });
  }

  // Remap edges — all grouped runs point to the sweep node
  const newEdges: Edge[] = [];
  const seenEdgeKeys = new Set<string>();

  for (const edge of flowEdges) {
    const sourceGroup = runToGroup.get(edge.source);
    const source = sourceGroup ? `sweep-${sourceGroup.groupId}` : edge.source;

    const targetGroup = runToGroup.get(edge.target);
    const target = targetGroup ? `sweep-${targetGroup.groupId}` : edge.target;

    const edgeKey = `${source}->${target}`;
    if (seenEdgeKeys.has(edgeKey) || source === target) continue;
    seenEdgeKeys.add(edgeKey);

    newEdges.push({
      ...edge,
      id: source !== edge.source || target !== edge.target
        ? `grouped-${edgeKey}`
        : edge.id,
      source,
      target,
    });
  }

  return { nodes: newNodes, edges: newEdges };
}

// ── Reference Quantity Grouping ─────────────────────────────────────

export interface RefQuantityGroup {
  groupId: string;
  title: string;
  quantities: DAGNode[];
  /** The geometry node that owns these ref quantities (via `defines` edge). */
  geometryId: string | null;
  parentIds: string[];
  /** Surface mesh nodes that are children of the same geometry (for auto-detect). */
  surfaceMeshNodes: DAGNode[];
  /** IDs of ref quantities that have a `defines` edge from geometry (truly geometry-derived). */
  geometryDefinedIds: Set<string>;
}

/**
 * Detect reference_quantity nodes and group them by parent geometry.
 *
 * Uses `defines` edges to associate each ref quantity with its geometry.
 * Returns one group per geometry. Orphan ref quantities (no `defines` edge)
 * are collected into a single fallback group.
 */
export function detectRefQuantityGroups(
  nodes: DAGNode[],
  edges: DAGEdge[],
): RefQuantityGroup[] {
  const refNodes = nodes.filter((n) => n.kind === "reference_quantity");
  if (refNodes.length === 0) return [];

  const anchorEdges = edges.filter(
    (e) => e.relationship === "defines" || e.relationship === "belongs_to",
  );
  const refIdToGeom = new Map<string, string>();
  for (const e of anchorEdges) {
    refIdToGeom.set(e.child_id, e.parent_id);
  }

  const geomNodeMap = new Map(nodes.filter((n) => n.kind === "geometry").map((n) => [n.id, n]));

  // Build a map of geometry → surface_mesh children for auto-detect
  const surfaceMeshNodes = nodes.filter((n) => n.kind === "surface_mesh");
  const geomToMeshes = new Map<string, DAGNode[]>();
  for (const mesh of surfaceMeshNodes) {
    for (const e of edges) {
      if (e.child_id === mesh.id && geomNodeMap.has(e.parent_id)) {
        let arr = geomToMeshes.get(e.parent_id);
        if (!arr) { arr = []; geomToMeshes.set(e.parent_id, arr); }
        arr.push(mesh);
        break;
      }
    }
  }

  // Map geometry path labels → node IDs for path-based fallback grouping
  const geomLabelToId = new Map<string, string>();
  for (const [id, node] of geomNodeMap) {
    const label = String(node.path).split(".").pop();
    if (label) geomLabelToId.set(label, id);
  }

  const byGeom = new Map<string, DAGNode[]>();
  const orphans: DAGNode[] = [];
  for (const rq of refNodes) {
    let geomId = refIdToGeom.get(rq.id);

    // Fallback: infer geometry from path prefix (reference.{geom_label}.{param})
    if (!geomId) {
      const segments = String(rq.path).split(".");
      if (segments.length >= 3 && segments[0] === "reference") {
        geomId = geomLabelToId.get(segments[1]!);
      }
    }

    if (geomId) {
      let arr = byGeom.get(geomId);
      if (!arr) { arr = []; byGeom.set(geomId, arr); }
      arr.push(rq);
    } else {
      orphans.push(rq);
    }
  }

  const geometryDefinedIds = new Set(refIdToGeom.keys());
  const childToParents = buildChildToParents(edges);
  const groups: RefQuantityGroup[] = [];

  for (const [geomId, quantities] of byGeom) {
    const geomNode = geomNodeMap.get(geomId);
    const geomName = geomNode ? String(geomNode.path).split(".").pop() : "?";
    const parentIds = new Set<string>();
    for (const n of quantities) {
      for (const pid of childToParents.get(n.id) ?? []) parentIds.add(pid);
    }
    groups.push({
      groupId: `ref-${geomId}`,
      title: `Ref Quantities · ${geomName} (${quantities.length})`,
      quantities,
      geometryId: geomId,
      parentIds: [...parentIds],
      surfaceMeshNodes: geomToMeshes.get(geomId) ?? [],
      geometryDefinedIds,
    });
  }

  if (orphans.length > 0) {
    const parentIds = new Set<string>();
    for (const n of orphans) {
      for (const pid of childToParents.get(n.id) ?? []) parentIds.add(pid);
    }
    groups.push({
      groupId: "ref-orphans",
      title: `Reference Quantities (${orphans.length})`,
      quantities: orphans,
      geometryId: null,
      parentIds: [...parentIds],
      surfaceMeshNodes: [],
      geometryDefinedIds,
    });
  }

  return groups;
}

/** @deprecated Use `detectRefQuantityGroups` (plural). */
export function detectRefQuantityGroup(
  nodes: DAGNode[],
  edges: DAGEdge[],
): RefQuantityGroup | null {
  const groups = detectRefQuantityGroups(nodes, edges);
  return groups.length > 0 ? groups[0]! : null;
}

/**
 * Replace individual reference_quantity nodes with per-geometry group nodes.
 */
export interface RefQuantityGroupCallbacks {
  onSelectNode?: (nodeId: string) => void;
  onEditQuantity?: (quantity: DAGNode, geometryId: string | null) => void;
  onAutoDetect?: (meshNode: DAGNode, geometryId: string) => void;
  onToggleRefGroup?: (groupId: string) => void;
}

export function applyRefQuantityGroups(
  flowNodes: Node[],
  flowEdges: Edge[],
  groups: RefQuantityGroup[],
  callbacks?: RefQuantityGroupCallbacks,
  expandedRefGroups?: Set<string>,
): CollapseResult {
  if (groups.length === 0) return { nodes: flowNodes, edges: flowEdges };

  const allRefIds = new Set<string>();
  const refToGroupId = new Map<string, string>();
  for (const g of groups) {
    for (const rq of g.quantities) {
      allRefIds.add(rq.id);
      refToGroupId.set(rq.id, `refgroup-${g.groupId}`);
    }
  }

  const newNodes: Node[] = [];
  const insertedGroups = new Set<string>();

  for (const node of flowNodes) {
    if (!allRefIds.has(node.id)) {
      newNodes.push(node);
      continue;
    }
    const groupNodeId = refToGroupId.get(node.id)!;
    if (insertedGroups.has(groupNodeId)) continue;
    insertedGroups.add(groupNodeId);

    const group = groups.find((g) => `refgroup-${g.groupId}` === groupNodeId)!;
    const AREA_PARAMS = new Set(["wing_area", "ref_area", "s_gross", "s_net"]);
    const LENGTH_PARAMS = new Set([
      "wing_span", "root_chord", "tip_chord", "standard_mean_chord", "ref_chord",
    ]);

    const labels = group.quantities.map((n) => {
      const param = String(n.path).split(".").pop() ?? "?";
      const val = n.value_scalar;
      const raw = String(n.units ?? "");
      let units = raw
        .replace("mesh_units_squared", "")
        .replace("**2", "²");
      if (!units) {
        if (AREA_PARAMS.has(param)) units = "m²";
        else if (LENGTH_PARAMS.has(param)) units = "m";
      }
      if (val === undefined || val === null) return String(param);
      const display = typeof val === "number" ? val.toFixed(2) : String(val);
      return `${param} = ${display}${units ? ` ${units}` : ""}`;
    });

    const isExpanded = expandedRefGroups?.has(group.groupId) ?? true;
    const refQuantitySize = getRefQuantityGroupSize({
      expanded: isExpanded,
      labelCount: labels.length,
      hasMeshButton: group.surfaceMeshNodes.length > 0,
    });

    newNodes.push({
      id: groupNodeId,
      type: "refQuantityGroup",
      data: {
        label: group.title,
        group,
        labels,
        expanded: isExpanded,
        onToggle: callbacks?.onToggleRefGroup
          ? () => callbacks.onToggleRefGroup!(group.groupId)
          : undefined,
        ...callbacks,
      },
      position: { x: 0, y: 0 },
      style: {
        background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
        color: "#fff",
        borderRadius: 8,
        padding: "8px 14px",
        fontSize: 12,
        border: "2px solid rgba(255,255,255,0.3)",
        boxShadow: "0 2px 8px rgba(249,115,22,0.3)",
        width: REF_QTY_GROUP_WIDTH,
        height: refQuantitySize.height,
        boxSizing: "border-box" as const,
      },
    });
  }

  const newEdges: Edge[] = [];
  const seenEdgeKeys = new Set<string>();

  for (const edge of flowEdges) {
    const source = refToGroupId.get(edge.source) ?? edge.source;
    const target = refToGroupId.get(edge.target) ?? edge.target;

    const edgeKey = `${source}->${target}`;
    if (seenEdgeKeys.has(edgeKey) || source === target) continue;
    seenEdgeKeys.add(edgeKey);

    newEdges.push({
      ...edge,
      id: source !== edge.source || target !== edge.target
        ? `refgroup-${edgeKey}`
        : edge.id,
      source,
      target,
    });
  }

  return { nodes: newNodes, edges: newEdges };
}

/** @deprecated Use `applyRefQuantityGroups` (plural). */
export function applyRefQuantityGroup(
  flowNodes: Node[],
  flowEdges: Edge[],
  group: RefQuantityGroup | null,
  onSelectNode?: (nodeId: string) => void,
): CollapseResult {
  return applyRefQuantityGroups(flowNodes, flowEdges, group ? [group] : [], { onSelectNode });
}

// ── All Data virtual node ───────────────────────────────────────────

/**
 * Inject a single terminal "All Data" node into the React Flow graph.
 *
 * Edges are added from every sweep group (or ungrouped run) and every
 * ref-quantity group to the All Data node. The ref-quantity edges carry
 * the label "corrects".
 */
export function injectAllDataNode(
  nodes: Node[],
  edges: Edge[],
  projectId: string,
  runCount: number,
  onClick: () => void,
  tableData?: {
    columns: { field: string; headerName: string }[];
    rows: Record<string, unknown>[];
    selectedNodeId?: string | null;
    onRowClick?: (nodeId: string) => void;
  },
  countLabel = "runs",
): CollapseResult {
  const allDataId = `all-data-${projectId}`;

  const allDataNode: Node = {
    id: allDataId,
    type: "allDataNode",
    data: {
      label: "All Data",
      runCount,
      countLabel,
      onClick,
      columns: tableData?.columns,
      rows: tableData?.rows,
      selectedNodeId: tableData?.selectedNodeId,
      onRowClick: tableData?.onRowClick,
    },
    position: { x: 0, y: 0 },
    style: {
      background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
      color: "#fff",
      borderRadius: 8,
      padding: "8px 0px 0px 0px",
      fontSize: 12,
      border: "2px solid rgba(255,255,255,0.3)",
      boxShadow: "0 2px 8px rgba(37,99,235,0.3)",
      width: ALL_DATA_NODE_WIDTH,
      boxSizing: "border-box" as const,
    },
  };

  const sweepGroupIds = new Set(
    nodes.filter((n) => n.type === "sweepGroup").map((n) => n.id),
  );

  const runsInSweeps = new Set<string>();
  for (const edge of edges) {
    if (sweepGroupIds.has(edge.source)) {
      runsInSweeps.add(edge.source);
    }
    if (sweepGroupIds.has(edge.target)) {
      runsInSweeps.add(edge.target);
    }
  }

  const newEdges: Edge[] = [];
  const seenSources = new Set<string>();

  const plotNodeIds = new Set(
    nodes
      .filter((n) => n.type === "plotNode" ||
        ((n.data as Record<string, unknown>)?.dagNode &&
         ((n.data as Record<string, unknown>).dagNode as { kind: string }).kind === "saved_plot"))
      .map((n) => n.id),
  );

  for (const node of nodes) {
    if (node.type === "sweepGroup") {
      if (!seenSources.has(node.id)) {
        seenSources.add(node.id);
        newEdges.push({
          id: `alldata-${node.id}`,
          source: node.id,
          target: allDataId,
          animated: true,
        });
      }
    } else if (node.type === "refQuantityGroup") {
      if (!seenSources.has(node.id)) {
        seenSources.add(node.id);
        newEdges.push({
          id: `alldata-${node.id}`,
          source: node.id,
          target: allDataId,
          label: "corrects",
          animated: true,
        });
      }
    } else if (node.type === "sourceGroup") {
      if (!seenSources.has(node.id)) {
        seenSources.add(node.id);
        newEdges.push({
          id: `alldata-${node.id}`,
          source: node.id,
          target: allDataId,
          animated: true,
        });
      }
    } else if (
      (node.data as Record<string, unknown>)?.dagNode &&
      ((node.data as Record<string, unknown>).dagNode as { kind: string }).kind === "run" &&
      !sweepGroupIds.has(node.id)
    ) {
      if (!seenSources.has(node.id)) {
        seenSources.add(node.id);
        newEdges.push({
          id: `alldata-${node.id}`,
          source: node.id,
          target: allDataId,
          animated: true,
        });
      }
    }
  }

  // Connect orphan scalar/result nodes (no incoming edges) to All Data
  const childIds = new Set(edges.map((e) => e.target));
  for (const node of nodes) {
    if (seenSources.has(node.id) || plotNodeIds.has(node.id)) continue;
    const dagNode = (node.data as Record<string, unknown>)?.dagNode as { kind?: string; source?: string } | undefined;
    if (!dagNode) continue;
    const isOrphanScalar = dagNode.kind === "scalar" && !childIds.has(node.id);
    if (isOrphanScalar && !seenSources.has(node.id)) {
      seenSources.add(node.id);
      newEdges.push({
        id: `alldata-orphan-${node.id}`,
        source: node.id,
        target: allDataId,
        animated: true,
      });
    }
  }

  // Wire saved_plot nodes downstream of All Data and remove stale
  // run→plot edges so the DAG shows plots sourcing from corrected data.
  const filteredEdges = edges.filter(
    (e) => !plotNodeIds.has(e.target),
  );

  for (const plotId of plotNodeIds) {
    newEdges.push({
      id: `alldata-plot-${plotId}`,
      source: allDataId,
      target: plotId,
      label: "visualized_in",
      animated: true,
    });
  }

  return {
    nodes: [...nodes, allDataNode],
    edges: [...filteredEdges, ...newEdges],
  };
}

// ── SDK source-file grouping ─────────────────────────────────────

export interface SourceGroup {
  groupId: string;
  title: string;
  sourceFile: string;
  nodes: DAGNode[];
  parentIds: string[];
}

/**
 * Group SDK-ingested nodes by their source file. Nodes from the same
 * CSV/input file collapse into a single group node. This reduces the
 * sprawling array/series graph into tidy source-based clusters.
 *
 * Only applies to nodes with kinds typical of SDK ingestion (dataframe,
 * series, parameter, result) that have a file:// source or sdk_ path prefix.
 */
export function detectSourceGroups(
  dagNodes: DAGNode[],
  dagEdges: DAGEdge[],
): SourceGroup[] {
  const SDK_KINDS = new Set(["dataframe", "series", "parameter", "result", "coefficient", "cache_entry"]);

  const sdkNodes = dagNodes.filter((n) => {
    const source = typeof n.source === "string" ? n.source : "";
    const path = typeof n.path === "string" ? n.path : "";
    return (
      SDK_KINDS.has(n.kind) &&
      !n.kind.startsWith("run") &&
      (source.startsWith("file://") || path.startsWith("sdk_"))
    );
  });

  if (sdkNodes.length < 3) return [];

  const sourceMap = new Map<string, DAGNode[]>();
  for (const node of sdkNodes) {
    let key: string;
    const source = typeof node.source === "string" ? node.source : "";
    const path = typeof node.path === "string" ? node.path : "";
    if (source.startsWith("file://")) {
      const filePath = source.replace("file://", "");
      key = filePath.split("/").pop() ?? filePath;
    } else if (path.startsWith("sdk_data.")) {
      const parts = path.split(".");
      key = parts[1] ?? "sdk_data";
    } else if (path.startsWith("sdk_derived.")) {
      key = "Derived Quantities";
    } else {
      key = "Other SDK Data";
    }
    if (!sourceMap.has(key)) sourceMap.set(key, []);
    sourceMap.get(key)!.push(node);
  }

  const childToParent = new Map<string, Set<string>>();
  for (const e of dagEdges) {
    if (!childToParent.has(e.child_id)) childToParent.set(e.child_id, new Set());
    childToParent.get(e.child_id)!.add(e.parent_id);
  }

  const groups: SourceGroup[] = [];
  for (const [source, nodes] of sourceMap) {
    if (nodes.length < 2) continue;

    const nodeIds = new Set(nodes.map((n) => n.id));
    const parentIds = new Set<string>();
    for (const node of nodes) {
      const parents = childToParent.get(node.id);
      if (parents) {
        for (const pid of parents) {
          if (!nodeIds.has(pid)) parentIds.add(pid);
        }
      }
    }

    groups.push({
      groupId: `source-${source.replace(/[^a-zA-Z0-9]/g, "_")}`,
      title: source,
      sourceFile: source,
      nodes,
      parentIds: [...parentIds],
    });
  }

  return groups;
}

/**
 * Collapse source groups into single nodes in the React Flow graph.
 */
export function applySourceGroups(
  flowNodes: Node[],
  flowEdges: Edge[],
  groups: SourceGroup[],
  expandedGroups: Set<string>,
  onToggleGroup: (groupId: string) => void,
): CollapseResult {
  if (groups.length === 0) return { nodes: flowNodes, edges: flowEdges };

  const nodeToGroup = new Map<string, SourceGroup>();
  for (const g of groups) {
    for (const n of g.nodes) nodeToGroup.set(n.id, g);
  }

  const newNodes: Node[] = [];
  const addedGroups = new Set<string>();

  for (const node of flowNodes) {
    const group = nodeToGroup.get(node.id);
    if (!group) {
      newNodes.push(node);
      continue;
    }
    if (addedGroups.has(group.groupId)) continue;
    addedGroups.add(group.groupId);

    const expanded = expandedGroups.has(group.groupId);
    const size = getSourceGroupSize({ expanded, nodeCount: group.nodes.length });

    newNodes.push({
      id: group.groupId,
      type: "sourceGroup",
      data: {
        label: `${group.title} (${group.nodes.length} fields)`,
        group,
        expanded,
        onToggle: () => onToggleGroup(group.groupId),
      },
      position: { x: 0, y: 0 },
      style: expanded
        ? {
            borderRadius: 12,
            fontSize: 12,
            width: size.width,
            height: size.height,
            boxSizing: "border-box" as const,
          }
        : {
            background: "linear-gradient(135deg, #1e3a5f 0%, #1a2f4a 100%)",
            color: "#B0D4C8",
            borderRadius: 8,
            padding: "8px 14px",
            fontSize: 12,
            border: "1px solid rgba(97,178,156,0.3)",
            boxShadow: "0 2px 8px rgba(30,58,95,0.3)",
            width: size.width,
            height: size.height,
            boxSizing: "border-box" as const,
          },
    });
  }

  const seenEdges = new Set<string>();
  const newEdges: Edge[] = [];

  for (const edge of flowEdges) {
    const srcGroup = nodeToGroup.get(edge.source);
    const tgtGroup = nodeToGroup.get(edge.target);

    const newSrc = srcGroup ? srcGroup.groupId : edge.source;
    const newTgt = tgtGroup ? tgtGroup.groupId : edge.target;

    if (newSrc === newTgt) continue;

    const edgeKey = `${newSrc}→${newTgt}`;
    if (seenEdges.has(edgeKey)) continue;
    seenEdges.add(edgeKey);

    newEdges.push({
      ...edge,
      id: `src-grp-${edgeKey}`,
      source: newSrc,
      target: newTgt,
    });
  }

  return { nodes: newNodes, edges: newEdges };
}
